// The zone page shell: a full-bleed hero (art | video | 3D, the poster always first), then the spec's sections in
// order, all inside a root that carries the zone's theme tokens. Everything above the fold is built synchronously
// from the compiled spec, so the first content never waits on the network (budget: < 1 s); the hero's media and the
// catalogue-driven sections fill in after. Empty sections never show.

import { getCatalogue, type Catalogue, type Game } from "../catalogue.ts";
import { lazyCover } from "../components.ts";
import { h, link } from "../dom.ts";
import { posterCard } from "../pages/home.ts";
import { setTitle } from "../router.ts";
import { BUDGETS, reducedMotion, zoneVars } from "../tokens.ts";
import { promoBlock } from "./promo.ts";
import { allZones, heroDecor, heroScene, registerBuiltInSection, sectionRenderer, zoneById, zoneHref, type SectionContext, type SectionRender } from "./registry.ts";
import { zoneRowGames } from "./rules.ts";
import { countdownTarget, dateMs, mediaUrl, promoOpen, sectionParts, type Zone, type ZoneRow } from "./spec.ts";

// the User Timing marks the screenshot harness reads (aliased: the copy test reads `=>`…`<` spans as page text)
const timing = globalThis.performance;
/** mark names */
export const MARK_FIRST = "zone:first-content";
export const MARK_HERO = "zone:hero";

/** The route handler for `/z/:id` and each zone's own `/<slug>`. */
export function zoneRoute(idOrSlug?: string) {
  return (params: Record<string, string>, query: URLSearchParams): HTMLElement => {
    const z = zoneById(idOrSlug ?? params.id ?? "");
    if (!z) throw new Error("That zone doesn't exist.");
    return zonePage(z, { posterOnly: query.get("hero") === "poster" });
  };
}

export interface ZonePageOpts {
  /** keep the poster, never mount 3D or play video (the screenshot harness; saves the GPU) */
  posterOnly?: boolean;
  now?: number;
}

export function zonePage(z: Zone, opts: ZonePageOpts = {}): HTMLElement {
  setTitle(z.title);
  const reduce = reducedMotion();
  const now = opts.now ?? Date.now();
  const root = h("div", {
    class: "zone",
    "data-zone": z.id,
    "data-kind": z.kind,
    "data-motion": reduce ? "still" : z.theme.motion,
    "data-texture": z.theme.texture,
  });
  for (const [k, v] of Object.entries(zoneVars(z, reduce))) root.style.setProperty(k, v);

  let cat: Promise<Catalogue> | null = null;
  const catalogue = () => (cat ??= getCatalogue());
  const body = h("div", { class: "zone-body" });
  root.append(zoneHero(z, opts, reduce), body);

  let i = 0;
  for (const s of z.sections) {
    const { kind, arg } = sectionParts(s);
    const render = sectionRenderer(kind, z.id);
    if (!render) continue; // unknown kinds are skipped, in both clients
    const slot = h("div", { class: "zone-section", "data-section": kind, hidden: true, style: { "--i": String(i++) } as Partial<CSSStyleDeclaration> });
    body.append(slot);
    const ctx: SectionContext = { zone: z, arg, catalogue, now };
    const fill = (el: HTMLElement | null) => {
      if (!el) return slot.remove();
      slot.replaceChildren(el);
      slot.hidden = false;
    };
    try {
      const out = render(ctx);
      if (out instanceof Promise) out.then(fill, (e) => (console.warn(`zone ${z.id}: section ${s}:`, e), slot.remove()));
      else fill(out);
    } catch (e) {
      console.warn(`zone ${z.id}: section ${s}:`, e);
      slot.remove();
    }
  }

  // global chrome fits the zone: main.ts hides the games search while `body[data-zone]` is set
  document.body.dataset.zone = z.id;
  requestAnimationFrame(() => {
    if (root.isConnected) timing.mark(MARK_FIRST, { detail: z.id });
  });
  return root;
}

function zoneHero(z: Zone, opts: ZonePageOpts, reduce: boolean): HTMLElement {
  const decor = heroDecor(z);
  const poster = decor?.poster ?? mediaUrl(z.hero.poster);
  const [fx, fy] = z.hero.focal;
  const media = h("div", { class: "zone-hero-media" });
  const img = poster
    ? h("img", { class: "zone-hero-poster", src: poster, alt: decor?.alt ?? z.hero.alt, fetchpriority: "high", decoding: "async", style: { objectPosition: `${fx * 100}% ${fy * 100}%` } })
    : null;
  if (img) media.append(img);
  const hero = h(
    "section",
    { class: "zone-hero", "data-hero": z.hero.type, "aria-label": z.title },
    media,
    h("div", { class: "zone-texture", "aria-hidden": "true" }),
    h("div", { class: "zone-scrim", "aria-hidden": "true" }),
    decor?.text ??
      h(
        "div",
        { class: "zone-hero-text" },
        eyebrow(z) ? h("p", { class: "zone-eyebrow" }, eyebrow(z)) : null,
        h("h1", { class: "zone-title" }, z.title),
        z.tagline ? h("p", { class: "zone-tagline" }, z.tagline) : null,
      ),
    z.theme.sound ? soundToggle(z.theme.sound) : null,
  );

  // budget: the hero (poster) within 2.5 s; past that the palette's gradient stays and the page is never blank
  let done = false;
  const ready = () => {
    if (done) return;
    done = true;
    hero.classList.add("ready");
    timing.mark(MARK_HERO, { detail: z.id });
  };
  if (img) {
    img.addEventListener("load", ready, { once: true });
    img.addEventListener("error", () => hero.classList.add("no-poster"), { once: true });
    if (img.complete && img.naturalWidth) ready();
  }
  window.setTimeout(() => {
    if (!done) {
      hero.classList.add("late");
      console.warn(`zone ${z.id}: hero over budget (${BUDGETS.hero} ms)`);
    }
  }, BUDGETS.hero);

  decor?.after?.(hero, { posterOnly: !!opts.posterOnly, reducedMotion: reduce });
  if (opts.posterOnly) return hero;
  if (z.hero.type === "video" && z.hero.video && !reduce) {
    const v = h("video", { class: "zone-hero-video", src: mediaUrl(z.hero.video)!, poster: poster ?? null, muted: true, loop: true, playsinline: true, autoplay: true, preload: "metadata", "aria-hidden": "true" });
    v.muted = true;
    v.addEventListener("playing", () => v.classList.add("on"), { once: true });
    media.append(v);
  } else if (z.hero.type === "3d") {
    const load = heroScene(z.hero.scene);
    if (load) {
      // after first paint and the poster: three.js never competes with the first content
      const go = () => {
        if (!hero.isConnected) return;
        const host = h("div", { class: "zone-hero-3d", "aria-hidden": "true" });
        media.append(host);
        load()
          .then((mount) => mount(host, { zone: z, reducedMotion: reduce }))
          .then((scene) => {
            host.classList.add("on");
            const gone = new MutationObserver(() => {
              if (!hero.isConnected) {
                gone.disconnect();
                scene.dispose();
              }
            });
            gone.observe(document.getElementById("main") ?? document.body, { childList: true });
          })
          .catch((e) => (console.warn(`zone ${z.id}: 3D hero failed, keeping the poster`, e), host.remove()));
      };
      const idle = (window as unknown as { requestIdleCallback?: (f: () => void, o?: { timeout: number }) => void }).requestIdleCallback;
      if (idle) idle(go, { timeout: 1200 });
      else window.setTimeout(go, 300);
    }
  }
  return hero;
}

/** The line over the title: an event's dates, an archetype's "Corner"; platform zones have none (their title is enough). */
function eyebrow(z: Zone): string | null {
  if (z.kind === "event" && z.season) {
    const f = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
    // `to` is exclusive: the last day is the day before
    return `${f.format(dateMs(z.season.from))} – ${f.format(dateMs(z.season.to) - 86400000)}`;
  }
  return { event: "Event", platform: null, archetype: "Corner" }[z.kind];
}

/** Sound is opt-in: nothing plays until the player presses this, and it remembers nothing. */
function soundToggle(sound: { src: string; label: string }): HTMLElement {
  let audio: HTMLAudioElement | null = null;
  const btn: HTMLButtonElement = h(
    "button",
    {
      type: "button",
      class: "zone-sound",
      "aria-pressed": "false",
      "aria-label": `Play ${sound.label}`,
      onclick: () => {
        audio ??= Object.assign(new Audio(mediaUrl(sound.src)!), { loop: true, volume: 0.5 });
        const on = btn.getAttribute("aria-pressed") !== "true";
        if (on) void audio.play().catch(() => {});
        else audio.pause();
        btn.setAttribute("aria-pressed", String(on));
        btn.setAttribute("aria-label", `${on ? "Stop" : "Play"} ${sound.label}`);
        btn.textContent = on ? "Sound on" : "Sound off";
      },
    },
    "Sound off",
  );
  return btn;
}

// ---- built-in sections (both clients implement these kinds; exported so a zone can wrap one for itself) ----

function shelf(title: string | undefined, games: Game[], style: ZoneRow["style"]): HTMLElement {
  const head = title ? h("h2", { class: "zone-h2" }, title) : null;
  if (style === "spotlight" || style === "wide") {
    const list = style === "spotlight" ? games.slice(0, 1) : games.slice(0, 6);
    return h(
      "section",
      { class: `zone-shelf ${style}` },
      head,
      h(
        "ul",
        { class: "zone-wide", role: "list" },
        list.map((g) =>
          h(
            "li",
            null,
            link(`/g/${g.id}`, { class: "zone-wide-card", ...lazyCover(g, "full") }, h("span", { class: "zone-wide-text" }, h("strong", null, g.title), h("span", null, g.blurb || g.creator.name))),
          ),
        ),
      ),
    );
  }
  if (style === "compact") {
    return h(
      "section",
      { class: "zone-shelf compact" },
      head,
      h(
        "ol",
        { class: "zone-compact", role: "list" },
        games.slice(0, 10).map((g) => h("li", null, link(`/g/${g.id}`, { class: "zone-compact-item" }, h("span", { class: "thumb", ...lazyCover(g) }), h("span", null, h("strong", null, g.title), h("span", { class: "zone-by" }, g.creator.name))))),
      ),
    );
  }
  return h("section", { class: "zone-shelf poster" }, head, h("ul", { class: "poster-track zone-track", role: "list" }, games.map((g) => posterCard(g))));
}

/** Draw zone rows as shelves (the `rows`, `row:<n>` and `games:` sections); null when none has games. */
export async function rowSection(ctx: SectionContext, rows: ZoneRow[]): Promise<HTMLElement | null> {
  const cat = await ctx.catalogue();
  const out = rows.map((r, i) => ({ r, games: zoneRowGames(cat, r, `${ctx.zone.id}:${i}`, ctx.now) })).filter((x) => x.games.length > 0);
  if (!out.length) return null;
  return h("div", { class: "zone-rows" }, out.map(({ r, games }) => shelf(r.title, games, r.style)));
}

/** The `countdown[:<moment id | date>]` section; null once there's nothing ahead. */
export function countdown(ctx: SectionContext): HTMLElement | null {
  const target = countdownTarget(ctx.zone, ctx.arg, Date.now());
  if (!target) return null;
  const at = target.at;
  const cells = (["days", "hours", "minutes", "seconds"] as const).map((u) => [u, h("span", { class: "zone-count-n" }, "0")] as const);
  const el = h(
    "div",
    { class: "zone-countdown", role: "timer", "aria-label": `Starts ${new Date(at).toUTCString()}` },
    h("p", { class: "zone-eyebrow" }, target.label ? `${target.label} in` : "Starts in"),
    h("div", { class: "zone-count" }, cells.map(([u, n]) => h("div", { class: "zone-count-cell" }, n, h("span", { class: "zone-count-u" }, u)))),
  );
  let t = 0;
  let shown = false; // the first ticks run before the section is attached: only stop once it was on the page and left
  const tick = () => {
    const left = Math.max(0, at - Date.now());
    if (left === 0) {
      window.clearInterval(t);
      el.closest(".zone-section")?.remove();
      return;
    }
    if (el.isConnected) shown = true;
    else if (shown) return window.clearInterval(t); // the page went away
    const s = Math.floor(left / 1000);
    const vals = [Math.floor(s / 86400), Math.floor(s / 3600) % 24, Math.floor(s / 60) % 60, s % 60];
    cells.forEach(([, n], i) => (n.textContent = String(vals[i]).padStart(i ? 2 : 1, "0")));
  };
  t = window.setInterval(tick, 1000);
  tick();
  return el;
}

/**
 * Cross-promos: blocks pointing into other zones. Each shows in its own `from`/`to` when it has them, else while the
 * target is in season (`promoOpen`, the same rule as Home's portals).
 */
export async function promos(ctx: SectionContext): Promise<HTMLElement | null> {
  let rowIds: Set<string> | undefined;
  try {
    rowIds = new Set((await ctx.catalogue()).rows?.map((r) => r.id) ?? []);
  } catch {
    rowIds = undefined;
  }
  const list = ctx.zone.crossPromos
    .map((p) => ({ p, z: zoneById(p.zone) }))
    .filter((x): x is { p: (typeof x)["p"]; z: Zone } => !!x.z && x.z.id !== ctx.zone.id && promoOpen(x.p, x.z, ctx.now, rowIds));
  if (!list.length) return null;
  return h("div", { class: "zone-promos" }, list.map(({ p, z }) => promoBlock(z, p.placement, p.copy, undefined, undefined, p.arg)));
}

/**
 * The built-in kinds. Each is also in the registry (`builtInSection(kind)`), so a zone can wrap one with
 * `registerZoneSection(zoneId, kind, …)` and every other zone keeps the original.
 */
export const BUILT_IN_SECTIONS: Readonly<Record<string, SectionRender>> = {
  rows: (ctx) => rowSection(ctx, ctx.zone.rows),
  row: (ctx) => {
    const r = ctx.zone.rows[Number(ctx.arg) - 1];
    return r ? rowSection(ctx, [r]) : null;
  },
  games: async (ctx) => {
    const ids = (ctx.arg ?? "").split(",").filter((x) => x.startsWith("g_"));
    return ids.length ? rowSection(ctx, [{ games: ids, style: "poster" }]) : null;
  },
  countdown,
  promos,
  text: (ctx) => {
    const t = ctx.arg ? ctx.zone.copy[ctx.arg] : undefined;
    return t ? h("div", { class: "zone-text" }, t.split(/\n{2,}/).map((p) => h("p", null, p))) : null;
  },
};
Object.freeze(BUILT_IN_SECTIONS);
for (const [kind, render] of Object.entries(BUILT_IN_SECTIONS)) registerBuiltInSection(kind, render);

/** For tests and the harness: every zone id with its path. */
export function zoneIndex(): { id: string; href: string }[] {
  return allZones().map((z) => ({ id: z.id, href: zoneHref(z) }));
}
